/**
 * tenantAccent — tenant-accent colour math + the WCAG AA contrast guard for the signing ceremony's
 * binding primary controls (CB-5.11 / UX-DR3). Extracted from SignContractPageV2 so the derivation is
 * a SINGLE shared source of truth (the editor's tenant-primary surfaces can reuse it) and is
 * headlessly unit-testable.
 *
 * UX-DR3 ("never ship a failing binding control"): the tenant supplies an arbitrary brand accent. The
 * "Finish" / "Next field" / "Download" / consent controls render text on that accent, so the pair MUST
 * clear 4.5:1. `contrastSafeAccent` returns a guaranteed-compliant `{ background, foreground }`:
 *   1. white label on the raw accent if it already passes (dark/saturated brands),
 *   2. else dark-ink label on the raw accent if THAT passes (light brands — keep the brand hue),
 *   3. else auto-darken the accent toward black until a white label passes (the narrow mid-tone band
 *      where neither plain label clears AA — the case the old white-or-dark `accentInk` shipped failing).
 *
 * `accentTextOnLight` is the complementary form for accent used as TEXT/ICON on a light background
 * (secondary links/hints like "Apply to all" / "Change" / "Keep signing") — it darkens the accent
 * until accent-on-background clears AA. The ceremony's binding controls use `contrastSafeAccent`; this
 * module is the single source of truth for both. (Consumed by the signing ceremony today.)
 */

const WHITE = '#ffffff';
/** The ceremony's dark label ink (slate-900-ish), matching the rest of the trust-forward register. */
const DARK_INK = '#0f172a';
const AA_NORMAL = 4.5;

export interface SafeAccent {
  /** The (possibly darkened) accent to paint the control background. */
  background: string;
  /** A label colour guaranteed ≥4.5:1 against `background`. */
  foreground: string;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (c: number) => Math.max(0, Math.min(255, Math.round(c)));
  return '#' + [r, g, b].map((c) => clamp(c).toString(16).padStart(2, '0')).join('');
}

export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Contrast-guarded label ink (white vs dark) with the higher contrast against the accent. Retained for
 * callers that paint accent-on-a-known-good-background; prefer `contrastSafeAccent` for binding
 * controls, which can ALSO darken the background when neither ink clears AA.
 */
export function accentInk(accent: string): string {
  return contrastRatio(accent, WHITE) >= contrastRatio(accent, DARK_INK) ? WHITE : DARK_INK;
}

/**
 * The UX-DR3 guard for a binding control's background+label. Guaranteed to return a pair ≥4.5:1.
 * Pure + deterministic (computed at render per tenant theme), never baked into a static token.
 */
export function contrastSafeAccent(accent: string): SafeAccent {
  const base = hexToRgb(accent);
  // Malformed accent → fall back to the dark ink as a legible, on-register background with white text.
  if (!base) return { background: DARK_INK, foreground: WHITE };

  // 1. White label on the raw accent (dark / saturated brands).
  if (contrastRatio(accent, WHITE) >= AA_NORMAL) return { background: accent, foreground: WHITE };
  // 2. Dark-ink label on the raw accent (light brands — keep the exact brand hue).
  if (contrastRatio(accent, DARK_INK) >= AA_NORMAL) return { background: accent, foreground: DARK_INK };

  // 3. Mid-tone band: neither plain label clears AA. Darken the accent toward black (preserving the
  //    hue) until a WHITE label passes. White-on-black is 21:1, so this always converges quickly.
  let { r, g, b } = base;
  for (let i = 0; i < 24; i++) {
    r *= 0.9;
    g *= 0.9;
    b *= 0.9;
    const hex = rgbToHex(r, g, b);
    if (contrastRatio(hex, WHITE) >= AA_NORMAL) return { background: hex, foreground: WHITE };
  }
  // Defensive (unreachable in practice — near-black passes): a guaranteed-compliant fallback.
  return { background: DARK_INK, foreground: WHITE };
}

/**
 * The tenant accent made safe to use as TEXT/ICON color on a light background (default white):
 * returns the accent unchanged if it already clears AA there, else darkens it toward black until it
 * does. For secondary accent-colored text/links/hints — NOT for a label on an accent-filled control
 * (use `contrastSafeAccent` for that). Guaranteed ≥4.5:1 against `background`.
 */
export function accentTextOnLight(accent: string, background: string = WHITE): string {
  if (contrastRatio(accent, background) >= AA_NORMAL) return accent;
  const base = hexToRgb(accent);
  if (!base) return DARK_INK;
  let { r, g, b } = base;
  for (let i = 0; i < 24; i++) {
    r *= 0.9;
    g *= 0.9;
    b *= 0.9;
    const hex = rgbToHex(r, g, b);
    if (contrastRatio(hex, background) >= AA_NORMAL) return hex;
  }
  return DARK_INK;
}
